import { useCallback } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetHeaderLeftIcon,
  SheetTitle,
  SheetCustomContent,
  SheetFooter,
  EmptyState,
} from '@sweetspot/scramble-ds'
import { UpdateResourcePayload, updateResource } from '@sweetspot/shared/data-access/api-platform'
import { useMutation } from 'react-query'
import { Button } from '@sweetspot/scramble-ds/atoms'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useToasts } from 'react-toast-notifications'
import { zodResolver } from '@hookform/resolvers/zod'
import { useResourcesStore } from '../../../../../../hooks/resources/useResourcesStore'
import { SideBarMode } from '../../../../../../types'
import { useIsMobile } from '../../../../../../hooks/useIsMobile'
import ResourceFormFields from '../ResourceFormFields'
import { ResourceSchema } from '../validation'

export const EditResource = () => {
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const {
    selectedResource,
    toggleSideBar,
    updateResource: updateResourceInStore,
    setSelectedResource,
  } = useResourcesStore()
  const isMobile = useIsMobile()
  const toggleUpdateResourceSideBar = useCallback(() => {
    toggleSideBar(SideBarMode.EDIT_RESOURCE)
  }, [toggleSideBar])

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<z.infer<typeof ResourceSchema>>({
    resolver: zodResolver(ResourceSchema),
    defaultValues: {
      name: selectedResource?.name || '',
    },
    mode: 'onChange',
  })

  const isFormFilled = isDirty && isValid

  const updateResourceMutation = useMutation(
    (data: UpdateResourcePayload) => updateResource(data),
    {
      onSuccess: (updatedResource) => {
        updateResourceInStore(updatedResource)
        setSelectedResource(updatedResource)
        addToast(t('sentences.resourceUpdatedSuccess'), { appearance: 'success' })
        reset()
        toggleUpdateResourceSideBar()
      },
      onError: () => {
        addToast(t('errors.somethingWentWrongNoId'), { appearance: 'error' })
      },
    }
  )

  const onSubmit = (data: z.infer<typeof ResourceSchema>) => {
    if (!selectedResource) return
    updateResourceMutation.mutate({ ...data, resourceId: selectedResource.resourceId })
  }

  return (
    <Sheet open onOpenChange={toggleUpdateResourceSideBar}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="h-auto rounded-b-none rounded-t-xl md:rounded-none"
      >
        <SheetHeader className="rounded-b-none rounded-t-xl md:rounded-none">
          <SheetHeaderLeftIcon onClick={toggleUpdateResourceSideBar}>
            <i className="fa-regular fa-close" />
          </SheetHeaderLeftIcon>
          <SheetTitle>
            {t('sentences.editResource')}: {selectedResource?.name || ''}
          </SheetTitle>
        </SheetHeader>
        <SheetCustomContent className="text-content-base text-text-dark font-regular relative">
          {updateResourceMutation.isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <EmptyState
                className="bg-translucent-light-900 absolute inset-0 z-10 h-full max-w-full"
                loading
              />
            </div>
          )}
          <ResourceFormFields control={control} errors={errors} watch={watch} isFromEditSheet />
        </SheetCustomContent>
        <SheetFooter className="pt-0">
          <Button
            className="w-full"
            variant="primary"
            disabled={
              !isFormFilled || Object.keys(errors).length > 0 || updateResourceMutation.isLoading
            }
            onClick={() => {
              handleSubmit(onSubmit)()
            }}
          >
            {t('sentences.updateResource')}
          </Button>
          <Button
            onClick={toggleUpdateResourceSideBar}
            className="w-full"
            variant="clear-dark"
            disabled={updateResourceMutation.isLoading}
          >
            {t('cancel')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
